import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2, AlertTriangle, XCircle, Loader2, FileText, Wand2, Copy, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, MatchBadge } from "@/components/shared";
import { api, errMsg, salaryText, streamPost } from "@/lib/api";

const VERDICT = {
  likely_pass: ["Likely to pass ATS screening", "text-emerald-700 bg-emerald-50 border-emerald-200"],
  may_struggle: ["May struggle with some ATS systems", "text-amber-700 bg-amber-50 border-amber-200"],
  likely_filtered: ["Likely to be filtered out by ATS", "text-red-700 bg-red-50 border-red-200"],
};
const ICON = { pass: [CheckCircle2, "text-emerald-600"], warn: [AlertTriangle, "text-amber-500"], fail: [XCircle, "text-red-600"] };

export const Upload = ({ busy, onFile, note = "stored encrypted, never shown to recruiters" }) => {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  const pick = (f) => f && onFile(f);
  return (
    <Panel testid="resume-upload-panel">
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
        className={`flex flex-col items-center rounded-xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-blue-500 bg-blue-50" : "border-slate-300"}`}>
        {busy ? <Loader2 className="h-10 w-10 animate-spin text-blue-600" /> : <UploadCloud className="h-10 w-10 text-blue-600" />}
        <p className="mt-3 font-semibold">{busy ? "Reading your resume and matching roles…" : "Drop your resume here"}</p>
        <p className="mt-1 text-sm text-muted-foreground">PDF, Word (.docx) or .txt · up to 5 MB · {note}</p>
        <input ref={ref} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="resume-file-input" />
        <Button className="mt-5 rounded-full" disabled={busy} onClick={() => ref.current.click()} data-testid="resume-choose-file">Choose file</Button>
      </div>
    </Panel>
  );
};

export const AtsPanel = ({ r, children }) => {
  const [text, cls] = VERDICT[r.ats.verdict];
  return (
    <Panel testid="resume-ats-panel">
      <div className="flex flex-wrap items-center gap-4">
        <div className="font-mono text-5xl font-extrabold" data-testid="resume-ats-score">{r.ats.score}<span className="text-lg text-muted-foreground">/100</span></div>
        <div className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${cls}`} data-testid="resume-ats-verdict">{text}</div>
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><FileText className="h-3.5 w-3.5" />{r.file_name}</p>
      <ul className="mt-5 space-y-3">{r.ats.checks.map((c) => {
        const [I, ic] = ICON[c.status];
        return <li key={c.key} className="flex gap-3" data-testid={`resume-check-${c.key}`}><I className={`mt-0.5 h-5 w-5 shrink-0 ${ic}`} />
          <div><p className="text-sm font-medium">{c.label} <span className="font-mono text-xs text-muted-foreground">{c.points}/{c.max}</span></p><p className="text-sm text-slate-600">{c.advice}</p></div></li>;
      })}</ul>
      <p className="mt-5 text-xs text-muted-foreground">{r.ats.disclaimer}</p>
      {children}
    </Panel>
  );
};

const Rewrite = ({ initial }) => {
  const [text, setText] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    setText("");
    try { await streamPost("/resume/rewrite", {}, (c) => setText((t) => t + c)); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel testid="resume-rewrite-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h3 className="font-semibold">Fix my resume</h3>
          <p className="text-sm text-muted-foreground">JobPedestal Intelligence rewrites your summary, skills and weakest bullets for ATS, using the missing keywords only where your experience supports them. Uses 3 AI credits.</p></div>
        <Button className="rounded-full" disabled={busy || !!text} onClick={run} data-testid="resume-fix-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}{busy ? "Rewriting…" : text ? "Rewritten — upload an updated resume to redo" : "Fix my resume"}</Button>
      </div>
      {text && <>
        <pre className="mt-4 max-h-[560px] overflow-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-4 font-sans text-sm leading-relaxed" data-testid="resume-rewrite-output">{text}</pre>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" className="rounded-full" disabled={busy} onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Copied"))} data-testid="resume-rewrite-copy"><Copy className="mr-2 h-3.5 w-3.5" />Copy text</Button>
          <p className="text-xs text-muted-foreground">Review every line before using it. Fill in anything marked [add metric] with your real numbers.</p>
        </div></>}
    </Panel>
  );
};

const Detected = ({ r, onSave, saving }) => (
  <Panel testid="resume-detected-panel">
    <h3 className="font-semibold">What we found</h3>
    <p className="mt-2 text-sm"><span className="text-muted-foreground">Titles: </span>{r.detected.titles.join(" · ") || "None detected"}</p>
    <p className="mt-1 text-sm"><span className="text-muted-foreground">Experience: </span>{r.detected.years_experience != null ? `about ${r.detected.years_experience} years` : "Not detected"}</p>
    <div className="mt-3 flex flex-wrap gap-1.5">{r.detected.skills.map((s) => <span key={s} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs">{s}</span>)}</div>
    {r.keywords.missing.length > 0 && <><h4 className="mt-5 text-sm font-semibold">Keywords your best-matching roles use that your resume doesn't</h4>
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="resume-missing-keywords">{r.keywords.missing.map((s) => <span key={s} className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs text-amber-800">{s}</span>)}</div>
      <p className="mt-2 text-xs text-muted-foreground">Only add the ones that truly reflect your experience.</p></>}
    <Button variant="outline" className="mt-5 rounded-full" disabled={saving} onClick={onSave} data-testid="resume-save-profile">Use this resume for my profile & matching</Button>
  </Panel>
);

const Matches = ({ items }) => (
  <Panel testid="resume-matches-panel">
    <h3 className="mb-3 font-semibold">Open roles that best fit your background</h3>
    {items.length === 0 ? <p className="text-sm text-muted-foreground">No close matches yet — try adding target titles on your profile.</p> : items.map((j) => (
      <Link key={j.job_id} to={`/app/jobs/${j.job_id}`} className="flex items-center justify-between gap-3 border-b py-3 last:border-0 hover:bg-slate-50" data-testid={`resume-match-${j.job_id}`}>
        <div className="min-w-0"><p className="truncate font-medium">{j.title}</p><p className="truncate text-xs text-muted-foreground">{j.employer} · {j.location} · {salaryText(j)}</p></div>
        <MatchBadge match={j.match} />
      </Link>))}
    <Link to="/app/jobs" className="mt-3 inline-block text-sm font-medium text-blue-700" data-testid="resume-see-all-jobs">See all matching jobs →</Link>
  </Panel>
);

const PAID = ["cand_professional", "cand_careerpro", "cand_executive"];
const PERKS = ["ATS score out of 100 with a pass / struggle / filtered verdict", "9 checks with exact fixes for your file", "Keywords your best-matching roles use that you're missing",
  "Open US & Canada roles that fit your background", "\"Fix my resume\" AI rewrite"];

export const AtsLocked = ({ cta, to }) => (
  <Panel testid="resume-locked-panel">
    <div className="flex items-center gap-2 text-sm font-semibold text-blue-700"><Lock className="h-4 w-4" />Included with paid plans</div>
    <h3 className="mt-2 text-xl font-bold">Find out if your resume will pass the ATS</h3>
    <ul className="mt-4 space-y-2">{PERKS.map((p) => <li key={p} className="flex gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{p}</li>)}</ul>
    <Button className="mt-6 h-auto whitespace-normal rounded-full py-2.5" asChild data-testid="resume-upgrade-cta"><Link to={to}>{cta}</Link></Button>
  </Panel>
);

export default function ResumeCheck() {
  const { user } = useAuth();
  if (user.role !== "admin" && !PAID.includes(user.plan)) {
    return (
      <div data-testid="resume-check-page">
        <PageHeader eyebrow="Resume check" title="Upload your resume, get matched and ATS-checked" testid="resume-header">The resume ATS check is part of every paid plan.</PageHeader>
        <AtsLocked cta="Upgrade to unlock the ATS check" to="/app/billing" />
      </div>
    );
  }
  return <ResumeTool />;
}

function ResumeTool() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/resume/latest").then((x) => setR(x.data)).catch(() => {}); }, []);
  const upload = async (file) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { setR((await api.post("/resume/analyze", fd)).data); toast.success("Resume analyzed"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    setSaving(true);
    try { await api.post("/resume/apply-to-profile"); toast.success("Profile updated — your job matches now use this resume"); } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="resume-check-page">
      <PageHeader eyebrow="Resume check" title="Upload your resume, get matched and ATS-checked" testid="resume-header">
        We read your resume, find open roles that fit your background, and tell you whether applicant tracking systems are likely to read it correctly.
      </PageHeader>
      <div className="space-y-6">
        <Upload busy={busy} onFile={upload} />
        {r && <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0"><AtsPanel r={r} /><Detected r={r} onSave={save} saving={saving} /></div>}
        {r && <Rewrite key={r.analyzed_at} initial={r.rewrite} />}
        {r && <Matches items={r.matches} />}
      </div>
    </div>
  );
}
